import { benefits, comparisonData } from '@/mocks/categories';

export default function Comparison() {
  return (
    <section className="relative bg-background-100 py-14 md:py-20">
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-16 mb-12">
            <div className="lg:w-2/5">
              <p className="text-xs font-semibold text-secondary-600 uppercase tracking-widest mb-3">Compare &amp; Save</p>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-tight">
                AcBuy Spreadsheet <span className="italic text-primary-500">vs</span> Manual Search
              </h2>
            </div>
            <div className="lg:w-3/5">
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                Why spend hours searching when everything is already organized? The AcBuy Spreadsheet puts thousands of products, verified sellers, and buying guides right at your fingertips — saving you time, money, and headaches.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 mb-12">
            <div className="bg-background-50 rounded-2xl p-6 md:p-7">
              <div className="rounded-xl overflow-hidden mb-5">
                <img
                  src="https://readdy.ai/api/search-image?query=Clean%20minimalist%20fashion%20product%20photography%20with%20sneakers%20and%20accessories%20on%20warm%20cream%20background%2C%20soft%20lighting%2C%20editorial%20style%2C%20elegant%20flat%20lay%2C%20professional%20product%20display%2C%20neutral%20and%20earthy%20tones&width=700&height=500&seq=comp-card1-v3&orientation=landscape"
                  alt="Product comparison"
                  className="w-full h-48 object-cover object-top"
                />
              </div>
              <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-semibold mb-3">Comparison</span>
              <h3 className="text-lg font-bold text-foreground-950 mb-2">Feature Comparison</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">See exactly how the spreadsheet stacks up against doing everything manually.</p>
            </div>
            <div className="bg-foreground-950 rounded-2xl p-6 md:p-7">
              <div className="w-full h-48 rounded-xl bg-foreground-900 flex items-center justify-center mb-5">
                <div className="text-center">
                  <i className="ri-scales-3-line text-5xl text-accent-400 mb-3 block"></i>
                  <span className="text-accent-400 text-xs font-semibold uppercase tracking-wider">Side by Side</span>
                </div>
              </div>
              <span className="inline-block px-3 py-1 rounded-full bg-accent-900/40 text-accent-300 text-xs font-semibold mb-3">Analysis</span>
              <h3 className="text-lg font-bold text-background-50 mb-2">Detailed Breakdown</h3>
              <p className="text-sm text-background-50/60 leading-relaxed">A comprehensive look at time saved, accuracy gained, and value delivered.</p>
            </div>
            <div className="bg-background-50 rounded-2xl p-6 md:p-7">
              <div className="rounded-xl overflow-hidden mb-5">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20shopping%20concept%20with%20laptop%20and%20fashion%20items%20on%20warm%20cream%20background%2C%20clean%20aesthetic%2C%20soft%20natural%20lighting%2C%20organized%20product%20display%2C%20editorial%20photography%2C%20shopping%20bag%20and%20clothing&width=700&height=500&seq=comp-card3-v3&orientation=landscape"
                  alt="Shopping guide"
                  className="w-full h-48 object-cover object-top"
                />
              </div>
              <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-semibold mb-3">Guide</span>
              <h3 className="text-lg font-bold text-foreground-950 mb-2">Buying Guide Included</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">Get expert guidance on how to shop safely and find exactly what you need.</p>
            </div>
          </div>

          <div className="bg-background-50 rounded-2xl overflow-hidden border border-background-200/60">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-background-200/60">
                    <th className="text-left px-6 py-4 text-sm font-semibold text-foreground-950">Feature</th>
                    <th className="text-center px-6 py-4 text-sm font-semibold text-primary-600">
                      <div className="flex items-center justify-center gap-2">
                        <i className="ri-file-list-3-line"></i>
                        AcBuy Spreadsheet
                      </div>
                    </th>
                    <th className="text-center px-6 py-4 text-sm font-semibold text-foreground-500">
                      <div className="flex items-center justify-center gap-2">
                        <i className="ri-search-line"></i>
                        Manual Search
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonData.map((row, idx) => (
                    <tr key={row.feature} className={`border-b border-background-200/40 ${idx % 2 === 0 ? 'bg-background-50' : 'bg-background-100/50'}`}>
                      <td className="px-6 py-4 text-sm font-medium text-foreground-800">{row.feature}</td>
                      <td className="px-6 py-4 text-center">
                        {typeof row.acbuy === 'boolean' ? (
                          row.acbuy ? (
                            <span className="inline-flex items-center gap-1 text-accent-600 text-sm font-medium">
                              <i className="ri-check-line"></i> Yes
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-foreground-400 text-sm">
                              <i className="ri-close-line"></i> No
                            </span>
                          )
                        ) : (
                          <span className="text-sm font-medium text-accent-600">{row.acbuy}</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {typeof row.manual === 'boolean' ? (
                          row.manual ? (
                            <span className="inline-flex items-center gap-1 text-accent-600 text-sm font-medium">
                              <i className="ri-check-line"></i> Yes
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-foreground-400 text-sm">
                              <i className="ri-close-line"></i> No
                            </span>
                          )
                        ) : (
                          <span className="text-sm text-foreground-500">{row.manual}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mt-12">
            {benefits.map((benefit) => (
              <div key={benefit.title} className="text-center p-4">
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-xl bg-primary-100">
                  <i className={`${benefit.icon} text-xl text-primary-600`}></i>
                </div>
                <h4 className="text-sm font-semibold text-foreground-950 mb-1">{benefit.title}</h4>
                <p className="text-xs text-foreground-500 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}